import { useTranslation } from 'next-i18next';
import PropTypes from 'prop-types';
import { useCallback, useMemo, Fragment } from 'react';

import { showConfirmModal } from '@/redux/slices/gui.slice';
import { disconnectUser } from '@/redux/slices/auth.slice';

import { useAppDispatch } from '@/redux';
import { NS } from '@/constants';
import { getUrl } from '@/utils/sidebarUtils';
import { replaceForTranslationKey } from '@/utils/millesimaUtils';

import ActiveLink from '@/components/common/ActiveLink';
import Icon from '@/components/common/iconedLinks/Icon';

import styles from './SideBar.module.scss';
import { ACCOUNT_TABS } from '@/constants/account';
import { PAGES } from '@/constants/pages';

export const SideBar = ({ linksConfig, baseUrlPath, customClass = '' }) => {
  const { t } = useTranslation([NS.COMMON, NS.ADMIN]);
  const dispatch = useAppDispatch();

  const buildLink = (linkName: string, baseUrlPath: string): string | string[] => {
    if (linkName === ACCOUNT_TABS.DISCONNECT) {
      return 'header.logout.title';
    } else if (linkName === ACCOUNT_TABS.PRIMEUR_ALERT) {
      return 'alert.link';
    } else if (baseUrlPath === PAGES.PRIMEUR) {
      return [
        `alert.${linkName}`,
        `${NS.ADMIN}:${replaceForTranslationKey(linkName)}.link`
      ];
    } else if (linkName === ACCOUNT_TABS.CUSTOMER_LOYALTY) {
      return `account.${linkName}`;
    } else {
      return [
        `account.${linkName}.link`,
        `${NS.ADMIN}:${replaceForTranslationKey(linkName)}.link`
      ];
    }
  };

  const disconnectConfirm = useCallback(() => {
    dispatch(showConfirmModal({
      content: {
        cancel: t('forms.radio.camelFalse'),
        confirm: t('forms.radio.camelTrue'),
        content: t('header.logout.content'),
        title: t('header.logout.title'),
        confirmClick: () => dispatch(disconnectUser())
      }
    }));
  }, []);

  const linksSections = useMemo(() => (
    Object.values(linksConfig).map(
      (section: any[]) => section.filter((link) => link.display)).filter((section) => section.length > 0
    )
  ), [linksConfig]);

  return (
    <div className={`${styles['side-bar']}${customClass ? ' ' + customClass : ''}`} data-rbf="side-bar-myaccount" data-test-id="side-bar-myaccount">
      {linksSections.map((linksSection, sectionIndex) => (
        <Fragment key={sectionIndex}>
          <ul data-test-id={`links-${sectionIndex}`} data-rbf={`links-${sectionIndex}`}>
            {linksSection.map((link, linkIndex) => {
              const linkLabel = t(buildLink(link.name, baseUrlPath));
              const href = getUrl(link, baseUrlPath);

              return (
                <li key={linkIndex} className={styles.link}>
                  {link.icon ? (
                    <span className={styles.icon}>
                      <Icon src={link.icon} alt={`${t('icon', { ns: NS.ALTER })} - ${linkLabel}`} />
                    </span>
                  ) : (
                    <div className={styles.icon} />
                  )}

                  {link.name === ACCOUNT_TABS.DISCONNECT ? (
                    <span
                      className="cursor-pointer"
                      aria-label={linkLabel}
                      onClick={disconnectConfirm}
                      data-test-id="sidebar-confirm-disconnect"
                    >
                      {linkLabel}
                    </span>
                  ) : link.name === ACCOUNT_TABS.CUSTOMER_LOYALTY ? (
                    <ActiveLink
                      activeClassName={styles.active}
                      href={getUrl(link, '')}
                      ariaLabel={linkLabel}
                    >
                      <>
                        {linkLabel}
                      </>
                    </ActiveLink>
                  ) : (
                    <>
                      <ActiveLink
                        ariaLabel={linkLabel}
                        activeClassName={styles.active}
                        href={href}
                        data-rbf={linkLabel}
                        shallow={baseUrlPath === PAGES.PRIMEUR}
                      >
                        <>
                          {linkLabel}
                        </>
                      </ActiveLink>
                      {(link?.counter || link.counter === 0) ? (
                        <div className={styles.counter}>
                          <span className={styles.value}>{link?.counter}</span>
                        </div>
                      ) : null}
                    </>
                  )}
                </li>
              );
            })}
          </ul>
          {sectionIndex !== (linksSections.length - 1) && (
            <div className={styles.separator} data-test-id="separator"></div>
          )}
        </Fragment>
      ))}
    </div>
  );
};

SideBar.propTypes = {
  linksConfig: PropTypes.object.isRequired,
  baseUrlPath: PropTypes.string.isRequired,
  customClass: PropTypes.string
};

export default SideBar;
